/** The export menu on a markdown table in chat.
 *
 *  Pure glue. Everything below it already exists and is tested on its own:
 *  `tableExtract.ts` reads the table out of the hast node react-markdown hands
 *  the `table` override, `lib/export/xlsx.ts` and `lib/export/pdf.ts` write the
 *  two files from the same `{ header, rows, provenance }` shape,
 *  `lib/export/pdfCharset.ts` says which characters the PDF cannot print, and
 *  `lib/download.ts` hands the browser the bytes.
 *
 *  Spec: docs/superpowers/specs/2026-09-22-chat-table-exports-design.md,
 *  "UI contract" under Cross-cutting contracts.
 *
 *  This module also owns the table's positioning wrapper, and that is the whole
 *  reason it takes `children`. The control is pinned to the top-right of the
 *  table's lane, and the lane is an `overflow-x: auto` box: an absolutely
 *  positioned child of a scroll container scrolls WITH the content, so on a wide
 *  table the button slides off to the left and out of view. It therefore needs a
 *  positioning ancestor that does not scroll — which is this wrapper, sitting
 *  just outside the existing lane. `MarkdownView`'s scroll Box and its `sx` stay
 *  exactly as they were; cell styling still lives in the parent.
 */
import React, { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { Download } from "lucide-react";

import { downloadBlob } from "@/lib/download";
import { buildTableXlsx, type XlsxTableInput } from "@/lib/export/xlsx";
import { buildTablePdf, type PdfTableInput } from "@/lib/export/pdf";
import { unrenderableCharactersInTable } from "@/lib/export/pdfCharset";
import { PROVENANCE_CAVEAT_LINES } from "@/lib/export/provenance";
import { useCompany } from "@/hooks/useCompany";
import { useChatServicesOptional } from "../providers/ChatProvider";
import { extractTable, type ExtractedTable, type HastNode } from "./tableExtract";
import { useIsStreaming } from "./StreamingContext";
import { useUserQuestion } from "./UserQuestionContext";
import { usePortalScope } from "@/analytics/PortalScope";

/** Marks the root of one message's rendered markdown. Set by `MarkdownView`;
 *  read here to number the tables within that message. */
export const MARKDOWN_ROOT_ATTR = "data-markdown-root";

/** On the trigger button, so sibling controls can be counted in document order
 *  and so the lane's hover rule has something to select. */
const CONTROL_CLASS = "artie-table-export";

type ExportFormat = "xlsx" | "pdf";

/** Both writers take the same shape on purpose, so one call site serves both. */
type TableExportInput = XlsxTableInput & PdfTableInput;

const FORMAT_LABELS: Record<ExportFormat, string> = {
  xlsx: "Excel (.xlsx)",
  pdf: "PDF",
};

/** Named individually up to this many; past it the note would be longer than
 *  the menu (a CJK table has hundreds of distinct characters). */
const MAX_NAMED_CHARACTERS = 8;

const CAVEAT = PROVENANCE_CAVEAT_LINES.join(" ");

const LANE_SX: SxProps<Theme> = {
  position: "relative",
  // Reveal-on-hover, but ONLY where hovering is a thing. `:hover` never fires on
  // a coarse pointer, and the floating widget is explicitly a small-viewport
  // touch surface — a hover-only control is simply invisible there. So the base
  // state below is visible, and hiding is what gets conditioned. `:focus-within`
  // carries the keyboard, which has no hover either.
  "@media (hover: hover) and (pointer: fine)": {
    [`& .${CONTROL_CLASS}`]: { opacity: 0 },
    [`&:hover .${CONTROL_CLASS}, &:focus-within .${CONTROL_CLASS}`]: { opacity: 1 },
  },
};

const CONTROL_SX: SxProps<Theme> = {
  position: "absolute",
  top: 4,
  right: 4,
  zIndex: 1,
  opacity: 1,
  transition: "opacity 120ms ease",
  // The table's own header row runs underneath, so the control needs a ground
  // of its own to stay legible.
  bgcolor: "background.paper",
  border: (t: Theme) => `1px solid ${t.palette.divider}`,
  color: "text.secondary",
  "&:hover": { bgcolor: "action.hover" },
};

const MENU_PAPER_SX: SxProps<Theme> = { maxWidth: 340 };

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** `artie-table-YYYYMMDD-HHmm.xlsx`, with `-2`, `-3` … for later tables in the
 *  same message.
 *
 *  Local time, not UTC: the only person who reads this name is the one who just
 *  clicked, next to the clock on their own machine. */
function tableExportFilename(
  now: Date,
  ordinal: number,
  format: ExportFormat,
): string {
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const suffix = ordinal > 0 ? `-${ordinal + 1}` : "";
  return `artie-table-${stamp}${suffix}.${format}`;
}

/** Which table this is within its message, counting in document order.
 *
 *  Read off the DOM at click time rather than allocated at mount: an ordinal
 *  handed out during render is a side effect, and React is free to render a
 *  component twice (StrictMode does exactly that), which would number the same
 *  table twice and skip a number. The DOM, by the time someone clicks, is the
 *  document order — which is what the filename is claiming. */
function tableOrdinal(button: HTMLElement | null): number {
  const root = button?.closest(`[${MARKDOWN_ROOT_ATTR}]`);
  if (!root || !button) return 0;
  const controls = Array.from(root.querySelectorAll(`.${CONTROL_CLASS}`));
  const index = controls.indexOf(button);
  return index < 0 ? 0 : index;
}

/** The warning on the PDF item, or null when the table renders cleanly.
 *
 *  Unlike the PDF's own footer — which cannot name the characters, because one
 *  of them in the string would turn that very line into mojibake
 *  (`pdfCharset.ts`) — a DOM surface can show them, so it does. Naming `₹` is
 *  the difference between a warning someone can act on and one they ignore. */
function pdfCharsetNote(characters: string[]): string | null {
  if (characters.length === 0) return null;
  const named = characters.slice(0, MAX_NAMED_CHARACTERS).join(" ");
  const more = characters.length > MAX_NAMED_CHARACTERS ? " and others" : "";
  return `PDF cannot display ${named}${more} — use Excel for these values.`;
}

function TableExportControl({ node }: { node: HastNode }) {
  const question = useUserQuestion();
  const chat = useChatServicesOptional();
  const company = useCompany();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const menuScope = usePortalScope("table");
  const open = Boolean(anchor);

  // Extracted when the menu opens, not on every render: there is one of these
  // per table in the transcript, and nothing needs the cells until someone asks
  // for them. The charset scan rides the same trigger.
  const table: ExtractedTable | null = useMemo(
    () => (open ? extractTable(node) : null),
    [open, node],
  );
  const charsetNote = useMemo(
    () => (table ? pdfCharsetNote(unrenderableCharactersInTable(table)) : null),
    [table],
  );

  const runExport = useCallback(
    async (format: ExportFormat, extracted: ExtractedTable, button: HTMLElement | null) => {
      setBusy(true);
      try {
        const timestamp = new Date();
        const input: TableExportInput = {
          header: extracted.header,
          rows: extracted.rows,
          provenance: {
            company: company?.name ?? null,
            question,
            timestamp,
            sessionId: chat?.sessionId ?? null,
            // A table in prose carries no elision marker to read: the
            // `[…truncated` heuristic applies to an artifact BODY, and this one
            // is text the model wrote inline. Asserting truncation we cannot
            // detect would devalue the warning where it IS detected.
            truncated: false,
          },
        };
        const blob =
          format === "xlsx" ? await buildTableXlsx(input) : await buildTablePdf(input);
        downloadBlob(tableExportFilename(timestamp, tableOrdinal(button), format), blob);
      } catch (error) {
        // Soft fail, matching the other export affordances (SessionSidebar's
        // session export, ArtifactDocCard's PDF): a download that could not be
        // built is not worth taking the transcript down for.
        // eslint-disable-next-line no-console
        console.warn("table export failed", error);
      } finally {
        setBusy(false);
      }
    },
    [chat?.sessionId, company?.name, question],
  );

  const handleExport = (format: ExportFormat) => {
    // The anchor IS the trigger button, and it stays in the DOM — grab it before
    // closing, since the ordinal is counted off its position among its siblings.
    const button = anchor;
    const extracted = table;
    setAnchor(null);
    if (!extracted) return;
    void runExport(format, extracted, button);
  };

  return (
    <>
      <Tooltip title="Export table">
        <IconButton
          size="small"
          className={CONTROL_CLASS}
          sx={CONTROL_SX}
          aria-label="Export table"
          data-ph="table-open_export"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          {/* Busy shows as a spinner rather than a disabled button: a disabled
              child inside a Tooltip swallows its own events (MUI warns about
              exactly this), and a control that goes dead mid-export reads as a
              bug. The menu items carry the guard instead. */}
          {busy ? <CircularProgress size={14} /> : <Download size={14} />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: MENU_PAPER_SX }, list: menuScope }}
      >
        <MenuItem data-ph="export_xlsx" disabled={busy} onClick={() => handleExport("xlsx")}>
          <Typography variant="body2">{FORMAT_LABELS.xlsx}</Typography>
        </MenuItem>
        <MenuItem
          data-ph="export_pdf"
          disabled={busy}
          onClick={() => handleExport("pdf")}
          sx={{ alignItems: "flex-start" }}
        >
          <Box>
            <Typography variant="body2">{FORMAT_LABELS.pdf}</Typography>
            {charsetNote && (
              <Typography
                variant="caption"
                sx={{ display: "block", color: "warning.main", whiteSpace: "normal" }}
              >
                {charsetNote}
              </Typography>
            )}
          </Box>
        </MenuItem>
        {/* `component="li"`: a Menu renders a <ul>, whose content model admits
            list items only — a bare <hr> there is invalid markup. */}
        <Divider component="li" />
        {/* The caveat lands here, at the moment someone decides to trust the
            figures, rather than under every table — a permanent note is one
            people learn to stop reading. Last child so MenuList's initial focus
            still goes to a real item. */}
        <Box component="li" sx={{ px: 2, py: 1 }}>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary", whiteSpace: "normal" }}
          >
            {CAVEAT}
          </Typography>
        </Box>
      </Menu>
    </>
  );
}

/**
 * Wraps one markdown table's scroll lane and pins an export control to it.
 *
 * The control is absent — not disabled — while the turn is streaming. Mid-stream
 * the table arrives row by row, so an export taken then is a partial table that
 * looks complete; a disabled button invites the click and explains nothing.
 * `streaming` reaches here through `StreamingContext`, because the `table`
 * override that renders this lives in a module-scope const and cannot read
 * props or call hooks — the same route the user's question takes.
 */
export function TableExportMenu({
  node,
  children,
}: {
  /** The `<table>` hast node react-markdown passed the override. */
  node?: HastNode;
  /** The existing `overflow-x` scroll lane, untouched. */
  children: ReactNode;
}) {
  const streaming = useIsStreaming();
  return (
    <Box sx={LANE_SX}>
      {children}
      {/* No node means no table to read — react-markdown always supplies one,
          but an export button that cannot export is worse than none. */}
      {!streaming && node ? <TableExportControl node={node} /> : null}
    </Box>
  );
}
